import {
    Listbox,
    Transition,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
} from '@headlessui/react';
import useProfiles from 'flow/hooks/useProfiles';
import { twMerge } from 'tailwind-merge';
import React, { Fragment, useState } from 'react';
import Icon from '../ui-components/Icon';
import {
    ChannelKey,
    ContactKey,
    ExtensionKey,
    QueueKey,
    ReferKey,
} from 'flow/types/EntityKeys';

interface Option {
    id: string;
    name: string;
}

interface Props {
    profile: Option;
    onChange: (newValue: Option) => void;
    entityKey: ExtensionKey | ContactKey | ReferKey | ChannelKey | QueueKey;
}

const CalendarProfilePicker = ({ profile, onChange, entityKey }: Props) => {
    const { sortedProfiles } = useProfiles(entityKey);

    const [query, setQuery] = useState('');

    const profiles = sortedProfiles
        ?.map((profile) => ({
            id: profile.get('key'),
            name: profile.get('description'),
        }))
        ?.toArray() as Option[];

    const filteredProfiles =
        query === ''
            ? profiles
            : profiles.filter((profile) =>
                  profile.name
                      .toLowerCase()
                      .replace(/\s+/g, '')
                      .includes(query.toLowerCase().replace(/\s+/g, '')),
              );

    return (
        <Listbox value={profile} by="id" onChange={onChange}>
            <div className="relative w-full text-sm/4">
                <ListboxButton className="focus-ring w-full rounded-sm border-1 bg-gray-200 py-3 ps-3 pe-9 text-left text-gray-900">
                    {profile.name}
                    <div className="absolute inset-y-0 end-0 flex items-center pe-2">
                        <Icon
                            className="size-5 text-gray-600"
                            iconKey="arrow_down"
                            aria-hidden="true"
                        />
                    </div>
                </ListboxButton>

                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <ListboxOptions className="scrollbar absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                        {filteredProfiles.map((option) => (
                            <ListboxOption
                                key={option.id}
                                className={({ focus }) =>
                                    twMerge(
                                        'relative cursor-default py-2 ps-3 pe-9 select-none',
                                        focus ? 'bg-gray-200' : 'text-gray-900',
                                    )
                                }
                                value={option}
                            >
                                {({ selected }) => (
                                    <span
                                        className={twMerge(
                                            'truncate text-gray-500',
                                            selected && 'font-medium',
                                        )}
                                    >
                                        {option.name}
                                    </span>
                                )}
                            </ListboxOption>
                        ))}
                    </ListboxOptions>
                </Transition>
            </div>
        </Listbox>
    );
};

export default CalendarProfilePicker;
